import React from 'react';
import { graphql } from '../../../../generated';
import { useModal } from '../../../../components/Modal/PresentedModal';
import { useMutation } from '@apollo/client';
import { FormForm } from '../../../../components/Form/FormForm';
import { t } from '../../../../locales/translate';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { handleError } from '../../../../model/notification';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { FormRadioGroup } from '../../../../components/Form/Radio/FormRadioGroup';
import { FormNumberField } from '../../../../components/Form/NumberField/FormNumberField';
import { QueryNames } from '../../../../model/graphql';

const CREATE_VOUCHER_CODE = graphql(`
  mutation CreateVoucherCode($voucherCampaignId: ID!, $code: String!) {
    createVoucherCode(input: { voucherCampaignId: $voucherCampaignId, code: $code }) {
      voucherCode {
        id
      }
    }
  }
`);

const GENERATE_VOUCHER_CODES = graphql(`
  mutation GenerateVoucherCodes($voucherCampaignId: ID!, $amount: Int!) {
    generateVoucherCodes(input: { voucherCampaignId: $voucherCampaignId, amount: $amount }) {
      voucherCodes {
        id
        code
      }
    }
  }
`);

interface Props {
  voucherCampaignId: string;
}

export function VoucherCodeCreate({ voucherCampaignId }: Props): JSX.Element {
  const { create: translation } = t.page.voucherCampaign.voucherCodes;
  const { open, Modal, size, close } = useModal();

  const [create] = useMutation(CREATE_VOUCHER_CODE, { refetchQueries: [QueryNames.VOUCHER_CODES] });
  const [generate] = useMutation(GENERATE_VOUCHER_CODES, { refetchQueries: [QueryNames.VOUCHER_CODES] });

  const { fields, dirty, valid, onSubmit } = useForm<{
    quantity: 'SINGLE' | 'MULTIPLE';
    code: string;
    amount: number;
  }>({
    model: {
      quantity: 'SINGLE',
      code: '',
      amount: 1,
    },
    onSubmit: async ({ model }) => {
      if (model.quantity === 'SINGLE') {
        await create({
          variables: {
            voucherCampaignId,
            code: model.code,
          },
        });
      }

      if (model.quantity === 'MULTIPLE') {
        await generate({
          variables: {
            voucherCampaignId,
            amount: model.amount,
          },
        });
      }

      close();
    },
    validations: {
      code: ({ model }) => {
        if (model.quantity === 'SINGLE' && !model.code) {
          return ['required'];
        }
        return [];
      },
      amount: ({ model }) => {
        if (model.quantity === 'MULTIPLE') {
          if (!model.amount) {
            return ['voucher_codes_min_1'];
          }
          if (model.amount > 1000) {
            return ['voucher_codes_max_1000'];
          }
        }
        return [];
      },
    },
    onSubmitError: handleError,
  });

  return (
    <Modal open={open} title={translation.voucherCode} size={size}>
      <FormForm onSubmit={onSubmit} onClose={close} canSubmit={dirty && valid}>
        <FormRadioGroup
          {...fields.quantity}
          name="quantity"
          options={[
            { value: 'SINGLE', label: translation.enterCode },
            { value: 'MULTIPLE', label: translation.generateCodes },
          ]}
          onChange={(value) => {
            if (value === 'SINGLE') {
              fields.code.reset();
            }
            if (value === 'MULTIPLE') {
              fields.amount.reset();
            }
            if (value) {
              fields.quantity.onChange(value);
            }
          }}
        />
        {fields.quantity.value === 'SINGLE' ? (
          <FormTextField
            {...fields.code}
            name="code"
            label={translation.enterCode}
            description={translation.codeDescription}
            required
          />
        ) : (
          <FormNumberField {...fields.amount} label={translation.amountOfCodes} name="amount" required />
        )}
      </FormForm>
    </Modal>
  );
}
